// Bailed pilots under parachutes (roadmap #1). Chutes ride the bomb snapshot
// section with the chute flag bit; this renders each as a team-tinted curved
// canopy (a gore-paneled dome with real shroud lines) over a chunky little
// pilot in the harness, interpolated between snapshots like the bombs it shares
// a section with. One InstancedMesh per team so the silk reads friend-or-foe.

import * as THREE from "three";
import { mergeBoxes, TEAM_INFO } from "./planes.js";
import { bakedGeometry, loadModel, modelsEnabled } from "./models.js";
import { uploadInstances } from "./instance_upload.js";

const shade = (hex, k) => new THREE.Color(hex).multiplyScalar(k).getHex();

// Merge a list of colored geometry parts into one vertex-colored BufferGeometry
// (the InstancedMesh needs a single geometry). A part is { geo, color }; if
// `color` is omitted the part's own vertex colors are kept (the gore dome).
function mergeColoredParts(parts) {
  const positions = [], normals = [], colors = [];
  const c = new THREE.Color();
  for (const { geo, color } of parts) {
    const g = geo.index ? geo.toNonIndexed() : geo;
    const pos = g.getAttribute("position");
    const nor = g.getAttribute("normal");
    const own = g.getAttribute("color");
    if (color != null) c.set(color);
    for (let i = 0; i < pos.count; i++) {
      positions.push(pos.getX(i), pos.getY(i), pos.getZ(i));
      normals.push(nor.getX(i), nor.getY(i), nor.getZ(i));
      if (own && color == null) colors.push(own.getX(i), own.getY(i), own.getZ(i));
      else colors.push(c.r, c.g, c.b);
    }
    if (g !== geo) g.dispose();
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  out.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  out.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  return out;
}

// A shallow hemispherical canopy, painted in alternating radial gores per face
// so it reads as sewn parachute silk. rim sits at y=cy, apex at cy + R*flatten.
function goreDome(R, flatten, cy, silk, silkDark, nGores) {
  const g = new THREE.SphereGeometry(R, nGores * 2, 7, 0, Math.PI * 2, 0, Math.PI * 0.54).toNonIndexed();
  g.scale(1, flatten, 1);
  g.translate(0, cy, 0);
  const pos = g.getAttribute("position");
  const colors = [];
  const cS = new THREE.Color(silk), cD = new THREE.Color(silkDark);
  for (let i = 0; i < pos.count; i += 3) {
    let cx = 0, cz = 0; // face centroid azimuth → which gore panel it belongs to
    for (let k = 0; k < 3; k++) { cx += pos.getX(i + k); cz += pos.getZ(i + k); }
    const az = Math.atan2(cz, cx) + Math.PI;
    const col = Math.floor((az / (Math.PI * 2)) * nGores) % 2 ? cD : cS;
    for (let k = 0; k < 3; k++) colors.push(col.r, col.g, col.b);
  }
  g.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  return g;
}

// A thin cylinder (a shroud line) from a to b.
function strut(a, b, r) {
  const av = new THREE.Vector3(a[0], a[1], a[2]);
  const bv = new THREE.Vector3(b[0], b[1], b[2]);
  const len = av.distanceTo(bv);
  const g = new THREE.CylinderGeometry(r, r, len, 4).toNonIndexed();
  const q = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(0, 1, 0), bv.clone().sub(av).normalize(),
  );
  g.applyQuaternion(q);
  g.translate((av.x + bv.x) / 2, (av.y + bv.y) / 2, (av.z + bv.z) / 2);
  return g;
}

// A real curved parachute over a pilot at the local origin (built up-+Y so the
// chute hangs vertically; the server position is the pilot's, not the canopy's).
function chuteGeometry(team) {
  const t = TEAM_INFO[team] ?? TEAM_INFO[0];
  const silk = t.color;
  const silkDark = shade(silk, 0.72);
  const line = 0x20242a;
  const body = t.body;
  const head = 0xcaa678;
  const R = 3.4;       // canopy radius
  const flatten = 0.62; // shallow dome (wider than tall)
  const cy = 4.2;      // canopy rim height above the pilot
  const parts = [{ geo: goreDome(R, flatten, cy, silk, silkDark, 12) }];
  // Suspension lines from around the canopy rim, converging on the harness.
  const nLines = 10;
  const harnessY = 1.0;
  for (let i = 0; i < nLines; i++) {
    const a = (i / nLines) * Math.PI * 2;
    parts.push({
      geo: strut([Math.cos(a) * R * 0.97, cy + 0.05, Math.sin(a) * R * 0.97],
        [Math.cos(a) * 0.18, harnessY, Math.sin(a) * 0.18], 0.035),
      color: line,
    });
  }
  // The pilot slung in the harness at the origin (still chunky — it's tiny).
  const box = (w, h, d, x, y, z) => new THREE.BoxGeometry(w, h, d).translate(x, y, z);
  parts.push(
    { geo: box(0.8, 1.0, 0.6, 0, 0.2, 0), color: body },   // torso
    { geo: box(0.5, 0.5, 0.5, 0, 0.95, 0), color: head },  // head
    { geo: box(0.28, 0.85, 0.28, 0.22, -0.65, 0), color: shade(body, 0.8) }, // legs
    { geo: box(0.28, 0.85, 0.28, -0.22, -0.65, 0), color: shade(body, 0.8) },
  );
  return mergeColoredParts(parts);
}

// A little rubber dinghy with a survivor waving, team-tinted so you can read
// whose pilot is in the water.
function raftGeometry(team) {
  const t = TEAM_INFO[team] ?? TEAM_INFO[0];
  const hull = 0x24303a;
  const floor = shade(t.color, 0.85);
  const body = t.body;
  const head = 0xcaa678;
  return mergeBoxes([
    { w: 2.6, h: 0.4, d: 0.5, x: 0, y: 0, z: 1.05, color: hull },
    { w: 2.6, h: 0.4, d: 0.5, x: 0, y: 0, z: -1.05, color: hull },
    { w: 0.5, h: 0.4, d: 2.6, x: 1.05, y: 0, z: 0, color: hull },
    { w: 0.5, h: 0.4, d: 2.6, x: -1.05, y: 0, z: 0, color: hull },
    { w: 2.2, h: 0.15, d: 2.2, x: 0, y: -0.1, z: 0, color: floor },
    { w: 0.6, h: 0.7, d: 0.5, x: 0, y: 0.45, z: 0, color: body },   // survivor torso
    { w: 0.4, h: 0.4, d: 0.4, x: 0, y: 1.0, z: 0, color: head },    // head
    { w: 0.18, h: 0.7, d: 0.18, x: 0.36, y: 0.95, z: 0, color: body }, // waving arm
  ]);
}

// Floating survivors awaiting rescue (roadmap #2): a bobbing team-tinted raft
// ringed by a pulsing orange dye-marker disc both teams can see. Fed a plain
// list of {id,x,z,team} from the welcome + rescue events (they don't move, so
// no interpolation — just bob and pulse).
export class RescueMarkers {
  constructor(scene, waterY = 0, radiusM = 90) {
    this.waterY = waterY;
    this.radius = radiusM;
    this.dummy = new THREE.Object3D();
    this.time = 0;
    const ringGeo = new THREE.RingGeometry(0.78, 1.0, 40).rotateX(-Math.PI / 2);
    this.ringMat = new THREE.MeshBasicMaterial({
      color: 0xff8a1e, transparent: true, opacity: 0.6,
      side: THREE.DoubleSide, depthWrite: false, fog: false,
    });
    this.rings = new THREE.InstancedMesh(ringGeo, this.ringMat, 32);
    this.rings.frustumCulled = false;
    this.rings.count = 0;
    scene.add(this.rings);
    this.rafts = TEAM_INFO.map((_, team) => {
      const m = new THREE.InstancedMesh(raftGeometry(team), new THREE.MeshLambertMaterial({ vertexColors: true }), 32);
      m.frustumCulled = false;
      m.count = 0;
      scene.add(m);
      return m;
    });
    this.markers = [];
  }

  setMarkers(list) {
    this.markers = list;
  }

  update(dt) {
    this.time += dt;
    this.ringMat.opacity = 0.45 + 0.25 * (0.5 + 0.5 * Math.sin(this.time * 3));
    const counts = [0, 0];
    let ringN = 0;
    for (const m of this.markers) {
      const bob = Math.sin(this.time * 1.6 + m.x * 0.05) * 0.22;
      const team = (m.team ?? 0) & 1;
      if (counts[team] < 32) {
        this.dummy.position.set(m.x, this.waterY + 0.35 + bob, m.z);
        this.dummy.rotation.set(0, this.time * 0.25 + m.x, 0);
        this.dummy.scale.setScalar(1);
        this.dummy.updateMatrix();
        this.rafts[team].setMatrixAt(counts[team]++, this.dummy.matrix);
      }
      if (ringN < 32) {
        const pulse = this.radius * (0.55 + 0.45 * (0.5 + 0.5 * Math.sin(this.time * 3 + m.z * 0.05)));
        this.dummy.position.set(m.x, this.waterY + 0.12, m.z);
        this.dummy.rotation.set(0, 0, 0);
        this.dummy.scale.set(pulse, 1, pulse);
        this.dummy.updateMatrix();
        this.rings.setMatrixAt(ringN++, this.dummy.matrix);
      }
    }
    uploadInstances(this.rings, ringN);
    for (let t = 0; t < this.rafts.length; t++) uploadInstances(this.rafts[t], counts[t]);
  }
}

export class Chutes {
  constructor(scene, capacity = 48) {
    this.capacity = capacity;
    this.dummy = new THREE.Object3D();
    this.time = 0;
    this.meshes = TEAM_INFO.map((_, team) => {
      const m = new THREE.InstancedMesh(
        chuteGeometry(team),
        // DoubleSide so the canopy dome reads from below as it descends.
        new THREE.MeshLambertMaterial({ vertexColors: true, side: THREE.DoubleSide }),
        capacity,
      );
      m.frustumCulled = false;
      m.count = 0;
      scene.add(m);
      return m;
    });
    this.chutes = []; // {pos, vel, team}
    // The parachute model (art/ordnance/parachute.glb): gores alternating
    // plain silk and the team's, a pilot in harness. Its origin is the pilot's
    // feet; the server's position is the pilot, a metre up.
    if (modelsEnabled()) {
      loadModel("parachute").then((t) => {
        const drop = new THREE.Matrix4().makeTranslation(0, -1.0, 0);
        this.meshes.forEach((m, team) => {
          const old = m.geometry;
          m.geometry = bakedGeometry(t, { team, matrix: drop }).geometry;
          old.dispose();
        });
      }, () => {});
    }
  }

  /** Replace the chute set from bomb-section rows, keeping only the chute bit
   *  (row index 12). Rows: [x,y,z,vx,vy,vz,team,shell,torp,flare,msl,rocket,chute]. */
  setFromSnapshot(rows) {
    this.chutes.length = 0;
    for (const r of rows) {
      if (!r[12]) continue;
      this.chutes.push({
        pos: new THREE.Vector3(r[0], r[1], r[2]),
        vel: new THREE.Vector3(r[3], r[4], r[5]),
        team: r[6] & 1,
      });
    }
  }

  update(dt) {
    this.time += dt;
    const counts = [0, 0];
    for (const c of this.chutes) {
      c.pos.addScaledVector(c.vel, dt); // advect between snapshots, like bombs
      const team = c.team;
      const n = counts[team];
      if (n >= this.capacity) continue;
      // A slow pendulum sway so the canopy breathes instead of sitting rigid.
      const sway = Math.sin(this.time * 1.4 + c.pos.x * 0.08) * 0.11;
      const roll = Math.cos(this.time * 1.15 + c.pos.z * 0.08) * 0.09;
      this.dummy.position.copy(c.pos);
      this.dummy.rotation.set(sway, 0, roll);
      this.dummy.updateMatrix();
      this.meshes[team].setMatrixAt(n, this.dummy.matrix);
      counts[team] = n + 1;
    }
    for (let team = 0; team < this.meshes.length; team++) uploadInstances(this.meshes[team], counts[team]);
  }
}
